import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, LayoutDashboard, Trash2, Star, Sparkles } from 'lucide-react'
import api from '../../services/api'

export default function AnalyticsList() {
  const [dashboards, setDashboards] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [seeding, setSeeding] = useState(false)
  const [name, setName] = useState('')
  const navigate = useNavigate()

  const load = async () => {
    try {
      const { data } = await api.get('/analytics/dashboards')
      setDashboards(data.data)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const create = async () => {
    if (!name.trim()) return
    try {
      const { data } = await api.post('/analytics/dashboards', { name: name.trim(), layout: [] })
      navigate(`/analytics/${data.id}`)
    } catch {}
  }

  const seedDefaults = async () => {
    setSeeding(true)
    try {
      await api.post('/analytics/seed-defaults')
      await load()
    } catch {}
    setSeeding(false)
  }

  const remove = async (id: string, e: React.MouseEvent) => {
    e.preventDefault()
    if (!confirm('Delete this dashboard?')) return
    try {
      await api.delete(`/analytics/dashboards/${id}`)
      setDashboards(d => d.filter(x => x.id !== id))
    } catch {}
  }

  if (loading) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading…</div>

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>Analytics</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>Build custom dashboards with your HSEQ data</p>
        </div>
        <div className="flex items-center gap-2">
          {dashboards.length === 0 && (
            <button
              onClick={seedDefaults}
              disabled={seeding}
              className="btn btn-secondary flex items-center gap-2"
            >
              <Sparkles size={15} /> {seeding ? 'Loading…' : 'Load Pre-built'}
            </button>
          )}
          <button onClick={() => setCreating(true)} className="btn btn-primary flex items-center gap-2">
            <Plus size={16} /> New Dashboard
          </button>
        </div>
      </div>

      {creating && (
        <div className="card p-5 space-y-3" style={{ borderTop: '2px solid var(--accent)' }}>
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>New Dashboard</h2>
          <input
            autoFocus
            placeholder="Dashboard name…"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && create()}
          />
          <div className="flex gap-2">
            <button onClick={create} className="btn btn-primary">Create</button>
            <button onClick={() => { setCreating(false); setName('') }} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {dashboards.length === 0 && !creating ? (
        <div className="card p-12 text-center space-y-4">
          <div className="w-16 h-16 rounded-2xl mx-auto flex items-center justify-center" style={{ background: 'var(--accent-light)' }}>
            <LayoutDashboard size={32} style={{ color: 'var(--accent)' }} />
          </div>
          <div>
            <p className="font-medium" style={{ color: 'var(--text)' }}>No dashboards yet</p>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Load pre-built dashboards or create your own</p>
          </div>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <button
              onClick={seedDefaults}
              disabled={seeding}
              className="btn btn-primary flex items-center gap-2"
            >
              <Sparkles size={15} /> {seeding ? 'Loading…' : 'Load Pre-built Dashboards'}
            </button>
            <button onClick={() => setCreating(true)} className="btn btn-secondary flex items-center gap-2">
              <Plus size={15} /> Create Blank
            </button>
          </div>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Pre-built includes: Safety KPI Overview, Contractor Performance, Audit &amp; Compliance
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {dashboards.map(d => (
            <Link
              key={d.id}
              to={`/analytics/${d.id}`}
              className="card block group overflow-hidden"
              style={{ padding: 0, borderTop: `2px solid ${d.is_default ? 'var(--amber)' : 'var(--accent)'}` }}
            >
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: d.is_default ? 'rgba(245,146,30,0.12)' : 'var(--accent-light)' }}>
                      {d.is_default
                        ? <Star size={15} style={{ color: 'var(--amber)' }} />
                        : <LayoutDashboard size={15} style={{ color: 'var(--accent)' }} />}
                    </div>
                    <h3 className="font-semibold truncate" style={{ color: 'var(--text)' }}>{d.name}</h3>
                  </div>
                  <button
                    onClick={e => remove(d.id, e)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-red-50 flex-shrink-0"
                    style={{ color: 'var(--red)' }}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {d.description && <p className="text-xs mt-2 line-clamp-2" style={{ color: 'var(--text-muted)' }}>{d.description}</p>}
              </div>
              <div className="flex items-center gap-3 px-5 py-2.5" style={{ borderTop: '1px solid var(--border)', background: 'var(--bg)' }}>
                <span className="text-xs font-medium" style={{ color: 'var(--accent)' }}>
                  {Array.isArray(d.layout) ? d.layout.length : 0} widget{(Array.isArray(d.layout) ? d.layout.length : 0) !== 1 ? 's' : ''}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>· by {d.creator_name}</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
